import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditOffIcon from "@mui/icons-material/EditOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InboxIcon from "@mui/icons-material/Inbox";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { HttpStatusCode } from "axios";
import ErrorPage from "_shared/_errors/_views/error-page";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { touchTargetSx } from "_shared/_views/touch-target";
import Guid from "_shared/guids/guid";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import GrantRow, { GrantRowTable } from "permissions/_views/grant-row/grant-row";
import { type GrantRowModel, buildGrantRow, grantPeopleIds, levelOptions } from "permissions/_views/grant-row/grant-row-model";
import HolderCell from "permissions/_views/grant-row/holder-cell";
import { useGrantChanges } from "permissions/_views/grant-row/use-grant-changes";
import { type PersonNames, personNameState, removerNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { personReference } from "permissions/_views/targets/permission-target-state";
import KindRecordHoldersTable, { holdersSubject } from "permissions/_views/kinds/kind-record-holders-table";
import { kindBySlug, kindPermissionsPath, kindsOfResourceType } from "permissions/_views/kinds/permission-kinds-model";
import useKindHolders, { KIND_HOLDERS_PAGE_SIZE, type KindHolder } from "permissions/_views/kinds/use-kind-holders";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import ExportGrantsButton from "permissions/_views/export/export-grants-button";
import {
  REMOVED_JUST_NOW,
  REMOVE_ACCESS,
  SET_LEVEL,
  bulkAnnouncements,
  holdersCaption,
  kindAllows,
  kindLabel,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";

/**
 * Everyone who holds one kind of permission, at `/permissions/kinds/{slug}` (`docs/design/
 * permissions/PERMISSIONS_UI_DESIGN.md`, "Routes and gates"). It answers "who in the organization
 * may sign?" and replaces the matrix that compared many people at once.
 *
 * The header names the kind, says what it allows, and counts its holders. The people are read a page
 * at a time from the grantee view filtered to the kind's containment, most recently granted first,
 * and each person's grants are drawn as grant rows led by the person, as the record page draws them.
 * Rows can be chosen and changed together: **Remove access** and **Set level**.
 *
 * A per-record kind ("one document") lists its holders with how many records each holds; the
 * records themselves are on each person's page.
 */

const CONTENT_MAX = 1100;

/** Said beneath the 404 when the address names no kind. */
export const NO_SUCH_KIND = "No kind of permission at this address";

const Page = styled(Box)({ height: "100%", minHeight: 0, overflowY: "auto", overflowX: "hidden" });

const Content = styled(Box)(({ theme }) => ({
  maxWidth: CONTENT_MAX,
  margin: "0 auto",
  padding: theme.spacing(2),
  display: "grid",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2, 3) },
}));

/** Stays in view while rows are chosen, so the actions are never a scroll away from the rows. */
const BulkBar = styled(Paper)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 3,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
}));

/** A holder in a sentence: their name, or a phrase that reads in its place ("a former member"). */
function nameOf(names: PersonNames, userId: Guid): string {
  return personReference(personNameState(names, userId.value));
}

function RowSkeletons() {
  return (
    <Box aria-busy="true" aria-label="Loading holders" sx={{ display: "grid", gap: 1 }}>
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} variant="rectangular" height={56} />
      ))}
    </Box>
  );
}

type HolderRow = Readonly<{ key: string; holder: KindHolder; permission: Permission; model: GrantRowModel }>;

/** Where a holder stood on the page when this page first changed one of their grants. */
export type PinnedHolder = Readonly<{ holder: KindHolder; index: number }>;

/**
 * The holders to draw: the page the server answered, with every holder this page has changed held
 * where they stood when it changed them. The grantee view counts live grants only and orders people
 * by their newest grant, so without this a person whose last grant was just removed would drop off
 * the page, switch and all, on the next read, and a person granted again would jump to the top.
 */
export function pinnedHolders(holders: readonly KindHolder[], pinned: ReadonlyMap<string, PinnedHolder>): KindHolder[] {
  const current = new Map(holders.map((holder) => [holder.granteeUserId.value, holder]));
  const shown = holders.filter((holder) => !pinned.has(holder.granteeUserId.value));
  const byIndex = [...pinned.entries()].sort(([, left], [, right]) => left.index - right.index);
  for (const [userId, pin] of byIndex) {
    shown.splice(Math.min(pin.index, shown.length), 0, current.get(userId) ?? pin.holder);
  }
  return shown;
}

/** The distinct people among some rows, in the rows' order. */
function peopleOf(rows: readonly HolderRow[]): Guid[] {
  const seen = new Map<string, Guid>();
  for (const row of rows) seen.set(row.holder.granteeUserId.value, row.holder.granteeUserId);
  return [...seen.values()];
}

/** How many of the rows' people had every one of their rows changed. */
function peopleDone(rows: readonly HolderRow[], succeeded: ReadonlySet<string>): number {
  return peopleOf(rows).filter((person) =>
    rows.filter((row) => row.holder.granteeUserId.isEqualTo(person)).every((row) => succeeded.has(row.key)),
  ).length;
}

/**
 * The rows, person by person in the page's order: each person's live grants, strongest first as the
 * server returns them, and any this page has removed, which stay where they were until navigation, or
 * granted again, until the list holds the new grant.
 * A grant this page removed is drawn from the removal's answer even while the live list still holds
 * it, so its row turns to removed at once rather than when the list is next read. A grant granted
 * again is drawn only while the list lacks it: once the list holds it, the server's copy is the row,
 * so a later change of level or a removal works from its current version (#6799 review).
 */
function holderRows(
  holders: readonly KindHolder[],
  grants: readonly Permission[],
  removed: ReadonlyMap<string, Permission>,
  regranted: ReadonlyMap<string, Permission>,
  model: (permission: Permission) => GrantRowModel,
): HolderRow[] {
  const live = new Set(grants.map((grant) => grant.id?.value));
  const kept = [...removed.values(), ...regranted.values()];
  const rows: HolderRow[] = [];
  for (const holder of holders) {
    const own = [
      ...grants
        .filter((grant) => grant.granteeUserId.isEqualTo(holder.granteeUserId))
        .map((grant) => removed.get(grant.id?.value ?? "") ?? grant),
      ...kept.filter((grant) => grant.granteeUserId.isEqualTo(holder.granteeUserId) && !live.has(grant.id?.value)),
    ];
    for (const permission of own) {
      rows.push({ key: permission.id?.value ?? "", holder, permission, model: model(permission) });
    }
  }
  return rows;
}

function KindPermissions(props: Readonly<{ kind: PermissionKind; kinds: readonly PermissionKind[] }>) {
  const { kind, kinds } = props;
  const sessionUser = useSessionUser();
  const viewerUserId = sessionUser?.id ?? Guid.EMPTY;
  const mayAdministerPlatform = sessionUser?.canAdministerPlatform() ?? false;
  const readOnly = !(sessionUser?.canGrantSigningAuthority() ?? false);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageIndex = Math.max(0, Number.parseInt(searchParams.get("page") ?? "1", 10) - 1 || 0);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [levelMenu, setLevelMenu] = useState<HTMLElement | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [working, setWorking] = useState(false);
  const [pinned, setPinned] = useState<ReadonlyMap<string, PinnedHolder>>(new Map());

  const holders = useKindHolders(kind, pageIndex);
  const token = permissionAreaToken(kind.area);
  const label = kindLabel(kind);

  // Memoized on purpose: the names are looked up for these people, a pinned holder the server no
  // longer counts among them, and a fresh array each render would be a fresh lookup argument.
  const shownHolders = useMemo(() => pinnedHolders(holders.holders, pinned), [holders.holders, pinned]);
  const userIds = useMemo(() => {
    const ids = new Set(shownHolders.map((holder) => holder.granteeUserId.value));
    for (const grant of holders.grants) for (const id of grantPeopleIds(grant)) ids.add(id);
    return ids;
  }, [shownHolders, holders.grants]);
  const names = usePersonNames(userIds);

  const changes = useGrantChanges(
    { legalEntityId: sessionUser?.legalEntityId ?? Guid.EMPTY, nameOf: (userId) => nameOf(names, userId) },
    viewerUserId,
  );

  // A grant granted again here has a new id the list does not hold until it is read again; drawn
  // until then, its holder keeps a row meanwhile (#6668).
  const rows = holderRows(shownHolders, holders.grants, changes.removed, changes.regranted, (permission) =>
    buildGrantRow({
      kind,
      kinds,
      permission,
      viewerUserId,
      mayAdministerPlatform,
      // The template page refuses anybody without administration access.
      mayOpenTemplates: sessionUser?.canAccessAdministration() ?? false,
      grantor: personNameState(names, permission.grantorUserId.value),
      remover: removerNameState(names, permission),
    }),
  );
  // A row may be chosen when its switch could remove it: not locked, not already removed.
  const choosable = readOnly ? [] : rows.filter((row) => row.model.hasSwitch && row.model.state === "enabled");
  const chosen = choosable.filter((row) => selected.has(row.key));
  const bulkLevels = levelOptions(kind, {}, mayAdministerPlatform);

  const toggle = (key: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  /** Holds the rows' people where they stand now, before a change can move them off the page. */
  const pin = (changing: readonly HolderRow[]) =>
    setPinned((current) => {
      const next = new Map(current);
      for (const row of changing) {
        const userId = row.holder.granteeUserId.value;
        const index = shownHolders.findIndex((holder) => holder.granteeUserId.value === userId);
        if (!next.has(userId) && index >= 0) next.set(userId, { holder: row.holder, index });
      }
      return next;
    });

  const chosenPeople = peopleOf(chosen);

  const removeChosen = async () => {
    setConfirmingRemoval(false);
    setWorking(true);
    pin(chosen);
    const succeeded = new Set<string>();
    for (const row of chosen) if (await changes.remove(row.permission, kind, { quiet: true })) succeeded.add(row.key);
    setWorking(false);
    setSelected(new Set());
    const done = peopleDone(chosen, succeeded);
    enqueueSnackbar(bulkAnnouncements.removed(kind.name, done, chosenPeople.length), {
      variant: done === chosenPeople.length ? "success" : "warning",
      role: "status",
    });
  };

  const setLevelOfChosen = async (option: (typeof bulkLevels)[number]) => {
    setLevelMenu(null);
    setWorking(true);
    pin(chosen);
    const succeeded = new Set<string>();
    for (const row of chosen) if (await changes.changeLevel(row.permission, kind, option.level)) succeeded.add(row.key);
    setWorking(false);
    setSelected(new Set());
    const done = peopleDone(chosen, succeeded);
    enqueueSnackbar(bulkAnnouncements.changed(kind.name, option.label, done, chosenPeople.length), {
      variant: done === chosenPeople.length ? "success" : "warning",
      role: "status",
    });
  };

  // How many rows each person has here: a person with two grants of the kind has two checkboxes,
  // and each needs a name of its own.
  const rowsPerPerson = new Map<string, number>();
  for (const row of rows) {
    const userId = row.holder.granteeUserId.value;
    rowsPerPerson.set(userId, (rowsPerPerson.get(userId) ?? 0) + 1);
  }

  const goToPage = (index: number) => {
    setSelected(new Set());
    setSearchParams(index === 0 ? {} : { page: String(index + 1) });
  };

  const renderRow = (row: HolderRow) => {
    const name = nameOf(names, row.holder.granteeUserId);
    const removedHere = changes.removedJustNow.has(row.key) && row.permission.isDisabled;
    const model = removedHere ? { ...row.model, removedText: REMOVED_JUST_NOW } : row.model;
    const isChoosable = choosable.includes(row);
    const select = readOnly ? undefined : (
      <Checkbox
        checked={isChoosable && selected.has(row.key)}
        disabled={!isChoosable || working}
        onChange={(_, checked) => toggle(row.key, checked)}
        sx={touchTargetSx()}
        slotProps={{
          input: {
            "aria-label":
              (rowsPerPerson.get(row.holder.granteeUserId.value) ?? 0) > 1 ? `Choose ${name}, ${row.model.levelText}` : `Choose ${name}`,
          },
        }}
      />
    );
    return (
      <GrantRow
        key={row.key}
        row={model}
        subject="record"
        personName={name}
        personHeader={
          <HolderCell
            userId={row.holder.granteeUserId}
            individual={names.individuals.get(row.holder.granteeUserId.value.toLowerCase())}
            state={personNameState(names, row.holder.granteeUserId.value)}
            onRetry={names.retry}
            select={select}
          />
        }
        onRetryGrantor={names.retry}
        pending={changes.pending.has(row.key)}
        alert={changes.alerts.get(row.key)}
        highlight={changes.highlighted.has(row.key)}
        onSwitch={
          readOnly
            ? undefined
            : (enabled) => {
                pin([row]);
                void (enabled ? changes.grantAgain(row.permission, kind) : changes.remove(row.permission, kind));
              }
        }
        onChangeLevel={
          readOnly
            ? undefined
            : (level) => {
                pin([row]);
                void changes.changeLevel(row.permission, kind, level);
              }
        }
      />
    );
  };

  // A per-record kind's holders are people and counts, which the shared table draws with its paging
  // and its empty and failed states. A standing kind's are grant rows: `GrantRowTable` is the one
  // layout every grant row has at every width, so the page draws those states in the table's words.
  let body: ReactNode;
  if (kind.perRecord) {
    body = (
      <KindRecordHoldersTable
        kind={kind}
        holders={holders}
        pageIndex={pageIndex}
        onPageChange={goToPage}
        names={names}
      />
    );
  } else if (holders.error) {
    body = (
      <Paper variant="outlined" role="alert" sx={{ p: 3, display: "grid", justifyItems: "center", gap: 1, textAlign: "center" }}>
        <ErrorOutlineIcon color="error" aria-hidden />
        <Typography component="h2" variant="h6">{`Couldn't load ${holdersSubject(kind)}`}</Typography>
        <Typography color="text.secondary" variant="body2">
          {holders.error}
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={holders.retry}>
          Retry
        </Button>
      </Paper>
    );
  } else if (holders.isLoading) {
    body = <RowSkeletons />;
  } else if (shownHolders.length === 0) {
    body = (
      <Paper variant="outlined" role="status" sx={{ p: 3, display: "grid", justifyItems: "center", gap: 1, textAlign: "center" }}>
        <InboxIcon color="action" aria-hidden />
        <Typography component="h2" variant="h6">{`No ${holdersSubject(kind)}`}</Typography>
      </Paper>
    );
  } else {
    body = (
      <Paper variant="outlined">
        <GrantRowTable label={`Holders of ${kind.name}`}>{rows.map(renderRow)}</GrantRowTable>
      </Paper>
    );
  }

  const allChosen = choosable.length > 0 && chosen.length === choosable.length;

  return (
    <Page>
      <Content>
        <PermissionsSubjectHeader
          trail={[
            { label: "Permissions", to: "/permissions" },
            { label: "Kinds", to: "/permissions#kinds" },
          ]}
          title={label}
          avatar={
            <Avatar sx={{ bgcolor: "transparent", border: 1, borderColor: "divider" }}>
              <token.Icon sx={{ color: token.color }} aria-hidden />
            </Avatar>
          }
          secondary={`${kindAllows(kind)} · ${kind.area}`}
          actions={<ExportGrantsButton scope={{ subject: "kind", kind }} kinds={kinds} subject={kind.name} />}
        >
          <Typography variant="body2" color="text.secondary">
            {holders.total === undefined ? <Skeleton width="30%" /> : holdersCaption(kind.name, holders.total)}
          </Typography>
        </PermissionsSubjectHeader>
        {choosable.length > 0 && (
          <BulkBar variant="outlined" role="toolbar" aria-label={`Change the chosen holders of ${kind.name}`}>
            <Checkbox
              checked={allChosen}
              indeterminate={chosen.length > 0 && !allChosen}
              disabled={working}
              onChange={(_, checked) => setSelected(checked ? new Set(choosable.map((row) => row.key)) : new Set())}
              sx={touchTargetSx()}
              slotProps={{ input: { "aria-label": "Choose everyone on this page" } }}
            />
            <Typography variant="body2" sx={{ flex: "1 1 auto" }}>
              {chosen.length > 0 ? bulkAnnouncements.selected(chosen.length) : "Choose people to change them together"}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<EditOffIcon />}
              sx={touchTargetSx()}
              disabled={chosen.length === 0 || working}
              onClick={() => setConfirmingRemoval(true)}
            >
              {REMOVE_ACCESS}
            </Button>
            {bulkLevels.length > 0 && (
              <Button
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                sx={touchTargetSx()}
                disabled={chosen.length === 0 || working}
                aria-haspopup="menu"
                onClick={(event) => setLevelMenu(event.currentTarget)}
              >
                {SET_LEVEL}
              </Button>
            )}
          </BulkBar>
        )}
        {body}
        {!kind.perRecord && holders.total !== undefined && holders.total > KIND_HOLDERS_PAGE_SIZE && (
          <TablePagination
            component="div"
            count={holders.total}
            page={pageIndex}
            rowsPerPage={KIND_HOLDERS_PAGE_SIZE}
            rowsPerPageOptions={[]}
            onPageChange={(_, index) => goToPage(index)}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} people`}
            sx={touchTargetSx("& .MuiIconButton-root")}
          />
        )}
      </Content>
      <Menu anchorEl={levelMenu} open={Boolean(levelMenu)} onClose={() => setLevelMenu(null)}>
        {bulkLevels.map((option) => (
          <MenuItem key={option.label} onClick={() => void setLevelOfChosen(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={confirmingRemoval} onClose={() => setConfirmingRemoval(false)} aria-labelledby="remove-chosen-title">
        <DialogTitle id="remove-chosen-title">{bulkAnnouncements.confirmRemoval(kind.name, chosenPeople.length)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${chosenPeople.map((person) => nameOf(names, person)).join(", ")}. ${bulkAnnouncements.removalUndo}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingRemoval(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void removeChosen()}>
            {REMOVE_ACCESS}
          </Button>
        </DialogActions>
      </Dialog>
      {/* One polite region for every change this page makes, in the wording module's words. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        <span key={changes.announcement.key}>{changes.announcement.text}</span>
      </Box>
    </Page>
  );
}

/**
 * The route: gates on Member Permissions, and finds the kind. The kinds are what the page is named
 * from, so it waits for them, and a failure to read them is the page's one error.
 */
export default function KindPermissionsPage() {
  const { slug } = useParams();
  const sessionUser = useSessionUser();
  const kindsQuery = useGetPermissionKindsQuery();

  if (!sessionUser) return null;
  if (!sessionUser.canReadMemberPermissions()) return <NotAuthorized />;
  if (kindsQuery.error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void kindsQuery.refetch()}>
            Retry
          </Button>
        }
      >
        Unable to load the kinds of permission. {getErrorMessageFromUnknown(kindsQuery.error)}
      </Alert>
    );
  }
  if (!kindsQuery.data) return <RowSkeletons />;

  const kind = kindBySlug(kindsQuery.data.kinds, slug);
  if (kind) return <KindPermissions key={kind.slug} kind={kind} kinds={kindsQuery.data.kinds} />;

  // An old address names a resource type where the slug goes (#6177). One kind of that type is the
  // page it meant; several are listed together on the home page's Kinds card.
  const ofType = kindsOfResourceType(kindsQuery.data.kinds, slug);
  if (ofType.length === 1) return <Navigate to={kindPermissionsPath(ofType[0])} replace />;
  if (ofType.length > 1) return <Navigate to="/permissions#kinds" replace />;

  // No kind at this address is an absence, not a refusal: it is drawn as the app's not-found page.
  return <ErrorPage error={{ status: HttpStatusCode.NotFound, statusText: NO_SUCH_KIND }} />;
}
